import type {
    ChannelDestination,
    ChannelTypeVerification,
    CommunicationPlan,
    CommunicationPlans,
    CustomPublicationSettings,
    OrganizationDestination,
    PublicationJob,
    PublicationMode,
} from '../types';
import { PUBLICATION_MODE } from '../types';

const isNil = (value: unknown): value is null | undefined => value === null || value === undefined;

const isEmptyValue = (value: unknown): boolean => {
    if (isNil(value)) {
        return true;
    }
    if (typeof value === 'string') {
        return value.length === 0;
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (typeof value === 'object') {
        return Object.keys(value as object).length === 0;
    }
    return false;
};

const sortByName = (organizations: OrganizationDestination[]): OrganizationDestination[] => {
    return [...organizations].sort((left, right) => {
        const leftKey = left.name ? left.name.toLowerCase() : -1;
        const rightKey = right.name ? right.name.toLowerCase() : -1;
        if (leftKey === rightKey) {
            return 0;
        }
        return leftKey < rightKey ? -1 : 1;
    });
};

const channelSortKey = (channel: ChannelDestination): string => {
    let key = channel.name ?? '';
    if (channel.subDestinationLabel) {
        key += channel.subDestinationLabel;
    }
    if (channel.subDestinationKey) {
        key += channel.subDestinationKey;
    }
    return key.toLowerCase();
};

const sortChannels = (channels: ChannelDestination[]): ChannelDestination[] => {
    return [...channels].sort((left, right) => {
        const leftKey = channelSortKey(left);
        const rightKey = channelSortKey(right);
        if (leftKey === rightKey) {
            return 0;
        }
        return leftKey < rightKey ? -1 : 1;
    });
};

const isPlanSupported = (
    plan: CommunicationPlan | undefined,
    channel: ChannelDestination,
    mode: PublicationMode,
): boolean => {
    if (!plan) {
        return false;
    }
    const actions = plan.planActions ?? {};
    const actionKey = mode === PUBLICATION_MODE.PUBLISH ? 'publish_action' : 'unpublish_action';
    const channelTypes = actions[actionKey] ?? [];
    return channel.type !== undefined && channelTypes.includes(channel.type);
};

const isPositiveOrganizationId = (organizationId: unknown): boolean => {
    return typeof organizationId === 'number' && Number.isFinite(organizationId) && organizationId > 0;
};

const isSameOrganizationId = (left: unknown, right: unknown): boolean => {
    if (isNil(left) || isNil(right)) {
        return false;
    }
    return String(left) === String(right);
};

const filterOrganizations = (
    contacts: OrganizationDestination[],
    currentOrganizationId?: number | string,
): OrganizationDestination[] => {
    return sortByName(contacts.filter((contact) => {
        return isPositiveOrganizationId(contact.organizationId) &&
            contact.state === 'ESTABLISHED' &&
            (isNil(contact.contactRole) || contact.contactRole !== 'DATA_SUPPLIER') &&
            !isSameOrganizationId(contact.itemNamespaceOrganizationId, currentOrganizationId);
    }));
};

const expandAndFilterChannels = ({
    channels,
    plans,
    verification,
    mode,
    translateFormat,
}: {
    channels: ChannelDestination[];
    plans: CommunicationPlans;
    verification: ChannelTypeVerification;
    mode?: PublicationMode;
    translateFormat?: (format: string) => string;
}): ChannelDestination[] => {
    const expanded: ChannelDestination[] = [];

    channels.forEach((channel) => {
        if (!channel.active ||
            channel.deleted ||
            !channel.type ||
            !verification[channel.type] ||
            channel.direction === 'INBOUND_ONLY') {
            return;
        }

        const prepared: ChannelDestination = { ...channel };
        let plan: CommunicationPlan | undefined;

        if (channel.plan) {
            plan = plans[channel.plan];
            if (mode && !isPlanSupported(plan, channel, mode)) {
                return;
            }
            prepared.planLabel = plan?.label || plan?.name;
        } else if (channel.format) {
            prepared.planLabel = translateFormat
                ? translateFormat(channel.format)
                : channel.format;
        }

        expanded.push(prepared);

        if (plan?.supportsSubDestinations && !isEmptyValue(channel.subDestinations)) {
            Object.entries(channel.subDestinations ?? {}).forEach(([subDestinationKey, subDestinationLabel]) => {
                const subChannel: ChannelDestination = {
                    ...prepared,
                    subDestinationKey,
                    subDestinationLabel,
                };
                delete subChannel.subDestinations;
                expanded.push(subChannel);
            });
        }
    });

    return sortChannels(expanded);
};

const applyCustomPublicationTargetFilter = ({
    organizations,
    communicationChannels,
    mode,
    customPublicationSettings,
    user,
    organization,
}: {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
    mode: PublicationMode;
    customPublicationSettings?: CustomPublicationSettings | null;
    user?: unknown;
    organization?: unknown;
}): {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
} => {
    if (!customPublicationSettings?.filterPublicationTargets) {
        return { organizations, communicationChannels };
    }

    const communicationChannelsWithType = communicationChannels.map((channel) => ({
        ...channel,
        destinationType: 'COMMUNICATION_CHANNEL' as const,
    }));
    const organizationsWithType = organizations.map((org) => ({
        ...org,
        destinationType: 'IN_PLATFORM' as const,
    }));
    const publicationTargets = [...communicationChannelsWithType, ...organizationsWithType];
    const filtered = customPublicationSettings.filterPublicationTargets(
        publicationTargets,
        mode,
        user,
        organization,
    );

    return {
        communicationChannels: filtered.filter(
            (target): target is ChannelDestination => target.destinationType === 'COMMUNICATION_CHANNEL',
        ),
        organizations: filtered.filter(
            (target): target is OrganizationDestination => target.destinationType === 'IN_PLATFORM',
        ),
    };
};

const applyDefaultPreselection = (
    organizations: OrganizationDestination[],
    communicationChannels: ChannelDestination[],
): {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
} => {
    if (organizations.length === 1 && communicationChannels.length === 0) {
        return {
            organizations: [{ ...organizations[0], isSelected: true }],
            communicationChannels,
        };
    }
    if (communicationChannels.length === 1 && organizations.length === 0) {
        return {
            organizations,
            communicationChannels: [{ ...communicationChannels[0], isSelected: true }],
        };
    }
    return { organizations, communicationChannels };
};

const includesId = (ids: Array<number | string> | undefined, value: number | string): boolean => {
    if (!ids) {
        return false;
    }
    return ids.some((id) => String(id) === String(value));
};

const applyJobPreselection = (
    organizations: OrganizationDestination[],
    communicationChannels: ChannelDestination[],
    job: PublicationJob,
): {
    organizations: OrganizationDestination[];
    communicationChannels: ChannelDestination[];
} => {
    const selectedOrganizations = organizations.map((org) => ({
        ...org,
        isSelected: !isEmptyValue(job.destinationOrganizationIds) &&
            includesId(job.destinationOrganizationIds, org.organizationId),
    }));

    const selectedChannels = communicationChannels.map((channel) => {
        let isSelected = !isEmptyValue(job.destinationChannelIds) &&
            includesId(job.destinationChannelIds, channel.id);
        if (!isEmptyValue(job.channelSubDestinations) && channel.subDestinationKey) {
            const match = (job.channelSubDestinations ?? []).some((entry) => {
                const [channelId, subKey] = String(entry).split(':');
                return String(channel.id) === channelId && channel.subDestinationKey === subKey;
            });
            if (match) {
                isSelected = true;
            }
        }
        return { ...channel, isSelected };
    });

    return {
        organizations: selectedOrganizations,
        communicationChannels: selectedChannels,
    };
};

export {
    applyCustomPublicationTargetFilter,
    applyDefaultPreselection,
    applyJobPreselection,
    expandAndFilterChannels,
    filterOrganizations,
    isEmptyValue,
    isNil,
    isPlanSupported,
};
